import { useLottiePlayer } from '@starsoft/common/hooks';
import styles from './styles.module.scss';
import { KYCUserIdentityFormKycSuccessProps } from './props';
import { Button } from '@starsoft/common/components';
import KYCUserIdentityFormKycSuccessSkeleton from './Skeleton';

/**
 * KYC User Identity Form Success Component
 * Displays a success message with animation after completing KYC verification.
 * Shows a loading skeleton while data is being fetched.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.description - Success message description text
 * @param {Function} props.onClick - Click handler for the action button
 * @param {string} props.title - Title text for the success message
 * @param {string} props.buttonLabel - Label text for the action button
 * @param {boolean} props.isLoading - Loading state flag
 * @returns {JSX.Element} Success message component or loading skeleton
 *
 * @example
 * <KYCUserIdentityFormKycSuccess
 *   description="Your identity has been verified successfully"
 *   onClick={() => function('Button clicked')}
 *   title="Verification Complete"
 *   buttonLabel="Continue"
 *   isLoading={false}
 * />
 */
export default function KYCUserIdentityFormKycSuccess({
  description,
  onClick,
  title,
  buttonLabel,
  isLoading,
}: KYCUserIdentityFormKycSuccessProps) {
  const { lottieAnimationRef } = useLottiePlayer({
    path: '/success.json',
  });

  if (isLoading) {
    return <KYCUserIdentityFormKycSuccessSkeleton />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__lottie} ref={lottieAnimationRef} />

      <div className={styles.container__wrapper}>
        <div className={styles.container__wrapper__title}>{title}</div>
        <div className={styles.container__wrapper__description}>
          {description}
        </div>
        <Button type="button" onClick={onClick}>
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}
